'use client';

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { LocationPicker, type ChosenPlace } from '@/components/maps/LocationPicker';
import { nameFromPlace } from '@/components/maps/place-view';
import { AccountField } from '@/components/platform/AccountField';
import { slugFromName } from '@/components/platform/format';
import { TENANT_BASE_DOMAIN } from '@/config';
import { notify } from '@/components/super-admin/ui';
import { api } from '@/lib/api';
import { ACCOUNT_EMAIL_MAX, ACCOUNT_NAME_MAX, ACCOUNT_PASSWORD_MAX, ACCOUNT_PASSWORD_MIN, accountError, leaveIfSignedOut } from '@/lib/platform/accounts';
import { parseCatalog, type CatalogPlan } from '@/lib/platform/catalog';
import { parseCreatedAgency, provisionStepError, type AgencyStep, type NewAgencyInput } from '@/lib/platform/provision';
import { SITE_DESIGNS } from '@/lib/storefront/designs';

const PASSWORD_HINT = 'At least 12 characters. It is not shown again.';
const STEPS: { id: AgencyStep; label: string }[] = [
  { id: 'identity', label: 'Identity' },
  { id: 'storefront', label: 'Storefront' },
  { id: 'plan', label: 'Plan' },
  { id: 'owner', label: 'Owner' },
];

export function NewAgencyPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [slugEdited, setSlugEdited] = useState(false);
  const [pending, setPending] = useState(false);
  const [plans, setPlans] = useState<CatalogPlan[]>([]);
  const [error, setError] = useState<string | null>(null);
  const filledName = useRef('');
  const [form, setForm] = useState<NewAgencyInput>({
    slug: '',
    name: '',
    shortName: '',
    city: '',
    address: '',
    latitude: null,
    longitude: null,
    phone: '',
    whatsapp: '',
    email: '',
    rc: '',
    customDomain: '',
    design: 'classic',
    primaryColor: '#FF8A00',
    secondaryColor: '#0d1117',
    ownerName: '',
    ownerEmail: '',
    ownerPassword: '',
    subscriptionTier: 'starter',
  });

  useEffect(() => {
    let cancelled = false;
    api<unknown>('/api/platform/plans')
      .then((payload) => {
        if (!cancelled) setPlans(parseCatalog(payload).filter((plan) => plan.active));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const current = STEPS[step] ?? STEPS[0];

  function applyPlace(place: ChosenPlace) {
    const address = place.address.trim() === '' ? place.city : place.address;
    setForm((currentForm) => {
      const incoming = nameFromPlace(currentForm.name, filledName.current, place.name);
      const name = incoming === null ? currentForm.name : incoming.slice(0, ACCOUNT_NAME_MAX);
      if (incoming !== null) filledName.current = name;
      return {
        ...currentForm,
        name,
        slug: incoming !== null && !slugEdited ? slugFromName(name) : currentForm.slug,
        city: place.city.slice(0, 80),
        address: address.slice(0, 500),
        latitude: place.latitude,
        longitude: place.longitude,
      };
    });
    setError(null);
  }

  function update<K extends keyof NewAgencyInput>(field: K, value: NewAgencyInput[K]) {
    setForm((currentForm) => {
      const next = { ...currentForm, [field]: value };
      if (field === 'name' && !slugEdited && typeof value === 'string') next.slug = slugFromName(value);
      if (field === 'slug') next.slug = String(value).toLowerCase();
      return next;
    });
    if (field === 'slug') setSlugEdited(true);
  }

  function goNext() {
    const invalid = provisionStepError(current.id, form);
    if (invalid) {
      setError(invalid);
      return;
    }
    setError(null);
    setStep((index) => Math.min(index + 1, STEPS.length - 1));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (step < STEPS.length - 1) {
      goNext();
      return;
    }
    const invalid = provisionStepError('owner', form);
    if (invalid) {
      setError(invalid);
      return;
    }
    setPending(true);
    setError(null);
    try {
      const created = parseCreatedAgency(await api<unknown>('/api/platform/tenants', {
        method: 'POST',
        body: JSON.stringify(createPayload(form)),
      }));
      try {
        await api(`/api/platform/tenants/${created.slug}/theme`, {
          method: 'PUT',
          body: JSON.stringify({
            intent: 'publish',
            design: form.design,
            primaryColor: form.primaryColor,
            secondaryColor: form.secondaryColor,
          }),
        });
        notify(`Agency created at ${created.host}.`);
      } catch (err: unknown) {
        if (leaveIfSignedOut(err)) return;
        notify(`${created.host} was created. Finish the storefront from Manage.`, 'error');
      }
      navigate(`/super-admin/agencies/${created.slug}`);
    } catch (err: unknown) {
      if (!leaveIfSignedOut(err)) setError(accountError(err, 'Could not create this agency.'));
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="mx-auto max-w-2xl space-y-4">
      <p className="text-sm text-muted">This creates the agency database and the first owner. Logo and favicon are uploaded from the agency page after the database exists.</p>
      <ol className="flex flex-wrap gap-2">
        {STEPS.map((item, index) => (
          <li key={item.id} className={`rounded-full px-3 py-1 text-xs font-semibold ${index === step ? 'bg-brand text-slate-950' : 'border border-line text-muted'}`}>
            {index + 1}. {item.label}
          </li>
        ))}
      </ol>
      <form onSubmit={(event) => void submit(event)} className="space-y-4 rounded-2xl border border-line bg-card p-5">
        {current.id === 'identity' && (
          <>
            <AccountField label="Legal name" value={form.name} onChange={(value) => update('name', value)} autoComplete="organization" maxLength={ACCOUNT_NAME_MAX} />
            <AccountField label="Trade name" value={form.shortName} onChange={(value) => update('shortName', value)} autoComplete="organization" maxLength={80} required={false} hint="Shown when the legal name is too long. Leave blank to use the legal name." />
            <AccountField label="Slug" value={form.slug} onChange={(value) => update('slug', value)} autoComplete="off" maxLength={63} hint="Lowercase letters, numbers, and hyphens." />
            {form.slug !== '' && (
              <p className="text-xs text-muted">
                The site is created at <span className="font-semibold text-ink">{`${form.slug}.${TENANT_BASE_DOMAIN || 'localhost'}`}</span>.
              </p>
            )}
            <LocationPicker
              value={form.latitude === null || form.longitude === null ? null : {
                name: form.name,
                city: form.city,
                address: form.address,
                country: '',
                latitude: form.latitude,
                longitude: form.longitude,
              }}
              onChange={(place) => applyPlace(place)}
              hint="Zoom or search, then choose a rental agency. Each choice replaces the legal name, city, and address."
              lookingLabel="Reading this point…"
              failedLabel="This point could not be read. Try another."
            />
            <AccountField label="Phone" value={form.phone} onChange={(value) => update('phone', value)} autoComplete="tel" maxLength={40} />
            <AccountField label="WhatsApp" value={form.whatsapp} onChange={(value) => update('whatsapp', value)} autoComplete="tel" maxLength={40} required={false} hint="Leave blank to use the phone number." />
            <AccountField label="Public email" value={form.email} onChange={(value) => update('email', value)} type="email" autoComplete="off" maxLength={ACCOUNT_EMAIL_MAX} />
            <AccountField label="RC" value={form.rc} onChange={(value) => update('rc', value)} autoComplete="off" maxLength={40} required={false} />
            <AccountField label="Custom domain" value={form.customDomain} onChange={(value) => update('customDomain', value)} autoComplete="off" maxLength={191} required={false} hint="Optional. Only plans that include a custom domain can connect one." />
          </>
        )}
        {current.id === 'storefront' && (
          <>
            <div className="grid gap-2">
              {SITE_DESIGNS.map((design) => (
                <button
                  key={design.id}
                  type="button"
                  onClick={() => update('design', design.id)}
                  className={`rounded-xl border px-3 py-2 text-left ${form.design === design.id ? 'border-brand' : 'border-line'}`}
                >
                  <span className="block text-sm font-medium text-ink">{design.label}</span>
                  <span className="text-xs text-muted">{design.tagline}</span>
                </button>
              ))}
            </div>
            <label className="block text-xs font-medium text-muted">
              Primary
              <input type="color" value={form.primaryColor} onChange={(event) => update('primaryColor', event.target.value)} className="mt-1 h-10 w-full rounded-xl border border-line bg-shell" />
            </label>
            <label className="block text-xs font-medium text-muted">
              Secondary
              <input type="color" value={form.secondaryColor} onChange={(event) => update('secondaryColor', event.target.value)} className="mt-1 h-10 w-full rounded-xl border border-line bg-shell" />
            </label>
          </>
        )}
        {current.id === 'plan' && (
          <label className="block text-xs font-medium text-muted">
            Plan
            <select
              value={form.subscriptionTier}
              onChange={(event) => update('subscriptionTier', event.target.value)}
              className="mt-1 w-full rounded-xl border border-line bg-shell px-3 py-2 text-sm text-ink outline-none focus:border-brand"
            >
              {(plans.length > 0 ? plans : [{ id: 'starter', name: 'Starter' }]).map((plan) => (
                <option key={plan.id} value={plan.id}>{plan.name}</option>
              ))}
            </select>
            <span className="mt-2 block text-xs text-muted">Vehicle and staff caps come from the plan. They can be changed later from the agency page.</span>
          </label>
        )}
        {current.id === 'owner' && (
          <>
            <AccountField label="Owner name" value={form.ownerName} onChange={(value) => update('ownerName', value)} autoComplete="name" maxLength={ACCOUNT_NAME_MAX} />
            <AccountField label="Owner email" value={form.ownerEmail} onChange={(value) => update('ownerEmail', value)} type="email" autoComplete="off" maxLength={ACCOUNT_EMAIL_MAX} hint="This is the login for the agency desk." />
            <AccountField
              label="Owner password"
              value={form.ownerPassword}
              onChange={(value) => update('ownerPassword', value)}
              type="password"
              autoComplete="new-password"
              minLength={ACCOUNT_PASSWORD_MIN}
              maxLength={ACCOUNT_PASSWORD_MAX}
              hint={PASSWORD_HINT}
            />
          </>
        )}
        {error && <p className="text-xs font-semibold text-danger">{error}</p>}
        <div className="flex flex-wrap gap-2">
          {step > 0 && (
            <button type="button" disabled={pending} onClick={() => { setError(null); setStep((index) => index - 1); }} className="rounded-full border border-line px-4 py-2 text-sm font-semibold text-ink">
              Back
            </button>
          )}
          {step < STEPS.length - 1 ? (
            <button type="button" onClick={goNext} className="rounded-full bg-brand px-4 py-2 text-sm font-semibold text-slate-950">
              Continue
            </button>
          ) : (
            <button type="submit" disabled={pending} className="rounded-full bg-brand px-4 py-2 text-sm font-semibold text-slate-950 disabled:opacity-50">
              {pending ? 'Creating…' : 'Create agency'}
            </button>
          )}
        </div>
      </form>
    </section>
  );
}

function createPayload(form: NewAgencyInput): Record<string, string | number> {
  const payload: Record<string, string | number> = {
    slug: form.slug,
    name: form.name.trim(),
    city: form.city.trim(),
    address: form.address.trim(),
    phone: form.phone.trim(),
    email: form.email.trim(),
    ownerName: form.ownerName.trim(),
    ownerEmail: form.ownerEmail.trim(),
    ownerPassword: form.ownerPassword,
    subscriptionTier: form.subscriptionTier,
    primaryColor: form.primaryColor,
    secondaryColor: form.secondaryColor,
  };
  if (form.latitude !== null && form.longitude !== null) {
    payload.latitude = form.latitude;
    payload.longitude = form.longitude;
  }
  if (form.shortName.trim() !== '') payload.shortName = form.shortName.trim();
  if (form.whatsapp.trim() !== '') payload.whatsapp = form.whatsapp.trim();
  if (form.rc.trim() !== '') payload.rc = form.rc.trim();
  if (form.customDomain.trim() !== '') payload.customDomain = form.customDomain.trim();
  return payload;
}
